<template>
  <!-- :sort-by="[{ key: 'project_number', order: 'asc' }]" -->
  <v-data-table :headers="headers" :items="filteredDeliverables">
    <template v-slot:top>
      <v-toolbar color="secondary" flat>
        <v-toolbar-title class="text-button">Custom Studies Catalog - Deliverable Inventory</v-toolbar-title>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" max-width="1000px">
          <template v-slot:activator="{ props }">
            <v-btn color="primary" dark class="mb-2" v-bind="props" v-if="this.initialized">New Item</v-btn>
          </template>
          <v-card>
            <v-form lazy-validation @submit.prevent="validateAndSave" ref="deliverable_form" :disabled="dialogDisabled">
              <v-card-title>
                <span class="text-h5">{{ formTitle }}</span>
              </v-card-title>
              <v-card-text class="error" v-if="failedSave">Failed to save study!</v-card-text>
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="6" md="4">
                      <v-select v-model="editedItem.group"
                                :rules="rules.group"
                                :items="groupItems"
                                v-if="editedIndex === -1"
                                :item-title="(item) => item.name"
                                :item-value="(item) => item.id"
                                label="Owner company" hide-selected small-chips>
                      </v-select>
                      <v-text-field v-model="getGroup(editedItem.group).name" :rules="rules.group" label="Owner company"
                                    :disabled="true" v-else></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-select v-model="editedItem.customer"
                                :rules="rules.customer"
                                :items="customerItems"
                                v-if="editedIndex === -1"
                                :item-title="(item) => item.name"
                                :item-value="(item) => item.id"
                                label="Customer" hide-selected small-chips>
                      </v-select>
                      <v-text-field v-model="getCustomer(editedItem.customer).name" :rules="rules.customer" label="Customer"
                                    :disabled="true" v-else></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field v-model="editedItem.project_name" :rules="rules.project_name" label="Project name"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field v-model="editedItem.project_number" :rules="rules.project_number" label="Project number"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-combobox v-model="editedItem.deliverable_type" v-model:search="deliverableTypeSearch"
                                  :rules="rules.deliverable_type"
                                  :hide-no-data="false"
                                  :items="deliverableTypeItems"
                                  hint="Select from pre-defined deliverable types or enter a new one"
                                  label="Deliverable type" hide-selected persistent-hint small-chips>
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title>
                              No results matching "<strong>{{ deliverableTypeSearch }}</strong>". Press <kbd>enter</kbd> to create a new one.
                            </v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-combobox>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-combobox v-model="editedItem.countries" v-model:search="countrySearch"
                                  :rules="rules.countries"
                                  :hide-no-data="false"
                                  :items="countryItems"
                                  hint="Select from pre-defined countries or enter a new one"
                                  label="Countries" hide-selected multiple persistent-hint small-chips>
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title>
                              No results matching "<strong>{{ countrySearch }}</strong>". Press <kbd>enter</kbd> to create a new one.
                            </v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-combobox>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-combobox v-model="editedItem.therapeutic_area" v-model:search="therapeuticAreaSearch"
                                  :rules="rules.therapeutic_area"
                                  :hide-no-data="false"
                                  :items="therapeuticAreaItems"
                                  hint="Select from pre-defined therapeutic areas or enter a new one"
                                  label="Therapeutic area" hide-selected persistent-hint small-chips>
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title>
                              No results matching "<strong>{{ therapeuticAreaSearch }}</strong>". Press <kbd>enter</kbd> to create a new one.
                            </v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-combobox>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-text-field v-model="editedItem.date" :rules="rules.date" label="Date of deliverable"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-combobox v-model="editedItem.tags" v-model:search="tagSearch"
                                  :rules="rules.tags"
                                  :hide-no-data="false"
                                  :items="tagItems"
                                  hint="Select from pre-defined tags or enter a new one"
                                  label="Tags" hide-selected multiple persistent-hint small-chips>
                        <template v-slot:no-data>
                          <v-list-item>
                            <v-list-item-title>
                              No results matching "<strong>{{ tagSearch }}</strong>". Press <kbd>enter</kbd> to create a new one.
                            </v-list-item-title>
                          </v-list-item>
                        </template>
                      </v-combobox>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-item-group multiple>
                        <div class="text-caption mb-2">Files for download</div>
                        <v-item v-for="file in editedItem.files" :key="file">
                          <v-chip @click="editedItem.files = editedItem.files.filter(item => item !== file)"
                                  :disabled="dialogDisabled">
                            {{ file }}
                            <v-btn icon="mdi-delete" variant="plain"></v-btn>
                          </v-chip>
                        </v-item>
                      </v-item-group>
                      <v-divider :vertical="true"></v-divider>
                      <v-file-input v-model="editedItem.newFiles" label="Additional files for download"
                                    placeholder="Upload your documents" prepend-icon="mdi-paperclip"
                                    :multiple="true">
                          <template v-slot:selection="{ fileNames }">
                            <template v-for="fileName in fileNames" :key="fileName">
                              <v-chip class="me-2" color="primary" size="small" label>
                                {{ fileName }}
                              </v-chip>
                            </template>
                          </template>
                        </v-file-input>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn variant="text" @click="close" :disabled="dialogDisabled">Cancel</v-btn>
                <v-btn type="submit" variant="text" :disabled="dialogDisabled">Save</v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card>
            <v-card-title>Confirm delete</v-card-title>
            <v-card-text class="error" v-if="failedDelete">Failed to delete study!</v-card-text>
            <v-card-text>Are you sure that you want to delete this study?</v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-card-actions class="mt-10">
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeDelete" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="deleteItemConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogShareWithCustomer" max-width="500px">
          <v-card>
            <v-card-title>Confirm sharing</v-card-title>
            <v-card-text class="error" v-if="failedSharing">Failed to share study!</v-card-text>
            <v-card-text class="mb-10">Are you sure that you want to share this study with the customer?</v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-divider :vertical="true"></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeShareWithCustomer" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="shareWithCustomerItemConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog v-model="dialogUnshareWithCustomer" max-width="500px">
          <v-card>
            <v-card-title>Confirm remove sharing</v-card-title>
            <v-card-text class="error" v-if="failedRemoveSharing">Failed to remove sharing of study!</v-card-text>
            <v-card-text class="mb-10">Are you sure that you want to remove sharing of this study with the customer?</v-card-text>
            <v-row>
              <v-col cols="12">
                <ProgressBar mode="indeterminate" style="height: 4px" :hidden="!dialogDisabled"></ProgressBar>
              </v-col>
            </v-row>
            <v-divider :vertical="true"></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn variant="text" @click="closeUnshareWithCustomer" :disabled="dialogDisabled">Cancel</v-btn>
              <v-btn variant="text" @click="unshareWithCustomerItemConfirm" :disabled="dialogDisabled">OK</v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn variant="text" icon="mdi-filter" @click.stop="filter_drawer = !filter_drawer"></v-btn>
      </v-toolbar>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <v-icon size="small" class="me-2" @click="editItem(item)" v-if="item.editable">mdi-pencil</v-icon>
      <v-icon size="small" class="me-2" @click="shareWithCustomer(item)" v-if="!item.shared_with_customer &&
        item.editable && (item.group !== item.customer)">mdi-account-plus</v-icon>
      <v-icon size="small" class="me-2" @click="unshareWithCustomer(item)" v-if="item.shared_with_customer &&
        item.editable">mdi-account-minus</v-icon>
      <v-icon size="small" @click="deleteItem(item)" v-if="item.editable">mdi-delete</v-icon>
    </template>
    <template v-slot:[`item.customer`]="{ item }">
      <v-chip-group :column="true">
        <div v-for="customer in [getCustomer(item.customer)]" :key="customer.id">
          <v-chip class="me-2" size="small" :label="true"
                  @click="toggleAllOneCustomers(customer)">{{ customer.name }}</v-chip>
          <v-icon class="me-2" size="small" v-if="item.shared_with_customer">mdi-account-outline</v-icon>
        </div>
      </v-chip-group>
    </template>
    <template v-slot:[`item.deliverable_type`]="{ item }">
      <v-chip-group :column="true">
        <v-chip class="me-2" size="small" :label="true"
                @click="toggleAllOneDeliverableTypes(item.deliverable_type)">{{ item.deliverable_type }}</v-chip>
      </v-chip-group>
    </template>
    <template v-slot:[`item.countries`]="{ item }">
      <v-chip-group :column="true">
        <v-chip v-for="country in item.countries" :key="country" class="me-2" color="primary" size="small"
                :label="true" @click="toggleAllOneCountries(country)">{{ country }}</v-chip>
      </v-chip-group>
    </template>
    <template v-slot:[`item.therapeutic_area`]="{ item }">
      <v-chip-group :column="true">
        <v-chip class="me-2" size="small" :label="true"
                @click="toggleAllOneTherapeuticAreas(item.therapeutic_area)">{{ item.therapeutic_area }}</v-chip>
      </v-chip-group>
    </template>
    <template v-slot:[`item.tags`]="{ item }">
      <v-chip-group :column="true">
        <v-chip v-for="tag in item.tags" :key="tag" class="me-2" color="primary" size="small"
                :label="true" @click="toggleAllOneTags(tag)">{{ tag }}</v-chip>
      </v-chip-group>
    </template>
    <template v-slot:[`item.files`]="{ item }">
      <v-chip-group :column="true">
        <v-chip v-for="file in item.files" :key="file" class="me-2" color="primary" size="small"
                @click="openFile(item.group, item.id, file)" :label="true">{{ file }}</v-chip>
      </v-chip-group>
    </template>
    <template v-slot:no-data>
      <v-progress-circular color="primary" v-if="!this.initialized" indeterminate></v-progress-circular>
      <v-card-text v-else>No data available</v-card-text>
    </template>
  </v-data-table>
  <v-navigation-drawer v-model="filter_drawer" location="right" v-bind:width="500">
    <v-card class="mx-auto epix-filter" elevation="0" ref="filters">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="filter_form">
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selectedCustomers"
                              :items="customerItems"
                              v-if="customerItems.length > 0"
                              :item-title="(item) => item.name"
                              :item-value="(item) => item.id"
                              label="Customers" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneCustomers">All/None</v-btn>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selectedDeliverableTypes" :items="deliverableTypeItems"
                              v-if="deliverableTypeItems.length > 0"
                              label="Deliverable Types" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneDeliverableTypes">All/None</v-btn>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selectedCountries" :items="countryItems"
                              v-if="countryItems.length > 0"
                              label="Countries" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneCountries">All/None</v-btn>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selectedTherapeuticAreas" :items="therapeuticAreaItems"
                              v-if="therapeuticAreaItems.length > 0"
                              label="Therapeutic Areas" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneTherapeuticAreas">All/None</v-btn>
                </v-col>
              </v-row>
              <v-divider :vertical="true"></v-divider>
              <v-row>
                <v-col cols="9">
                  <v-combobox v-model="selectedTags" :items="tagItems"
                              v-if="tagItems.length > 0"
                              label="Tags" :rules="[rules.required]" @click="$refs.filter_form.validate()"
                              multiple></v-combobox>
                </v-col>
                <v-col cols="3">
                  <v-btn variant="plain" @click="toggleAllNoneTags">All/None</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import {defineComponent} from "vue";
import axios from "axios";
import ProgressBar from 'primevue/progressbar';

function addItemsIfNotExists(array, items) {
    const set = new Set(array);
    if (!Array.isArray(items)) {
        items = [items];
    }
    items.forEach(item => {
        if (!set.has(item)) {
            array.push(item);
        }
    });
    array.sort()
}

export default defineComponent({
  name: 'CustomStudiesPage',
  components: {
    ProgressBar
  },
  data: () => ({
    initialized: false,
    filter_drawer: false,
    dialog: false,
    dialogDisabled: false,
    failedSave: false,
    dialogDelete: false,
    failedDelete: false,
    dialogShareWithCustomer: false,
    failedSharing: false,
    dialogUnshareWithCustomer: false,
    failedRemoveSharing: false,
    headers: [
      {
        title: 'Project name',
        align: 'start',
        sortable: false,
        key: 'project_name',
      },
      { title: 'Project number', key: 'project_number' },
      { title: 'Customer', key: 'customer' },
      { title: 'Deliverable type', key: 'deliverable_type' },
      { title: 'Countries', key: 'countries' },
      { title: 'Therapeutic area', key: 'therapeutic_area' },
      { title: 'Date of deliverable', key: 'date' },
      { title: 'Tags', key: 'tags' },
      { title: 'Files for download (ppt, doc, pdf, etc.)', key: 'files' },
      { title: 'Actions', key: 'actions', sortable: false },
    ],
    deliverables: [],
    filteredDeliverables: [],
    editedIndex: -1,
    tagItems: [],
    selectedTags: [],
    tagSearch: null,
    customerItems: [],
    selectedCustomers: [],
    therapeuticAreaItems: [],
    selectedTherapeuticAreas: [],
    therapeuticAreaSearch: null,
    countryItems: [],
    selectedCountries: [],
    countrySearch: null,
    deliverableTypeItems: [],
    selectedDeliverableTypes: [],
    deliverableTypeSearch: null,
    groupItems: [],
    editedItem: {},
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      },
      group: [
        value => {
          if (value) return true
          return 'You must enter an owner company.'
        }
      ],
      project_name: [
        value => {
          if (value) return true
          return 'You must enter a project name.'
        }
      ],
      project_number: [
        value => {
          if (value) return true
          return 'You must enter a project number.'
        }
      ],
      deliverable_type: [
        value => {
          if (value) return true
          return 'You must enter a deliverable type.'
        }
      ],
      countries: [
        value => {
          if (value && (value.length >= 1)) return true
          return 'You must enter at least one country.'
        }
      ],
      therapeutic_area: [
        value => {
          if (value) return true
          return 'You must enter a therapeutic area.'
        }
      ],
      customer: [
        value => {
          if (value) return true
          return 'You must enter a customer.'
        }
      ],
      date: [
        value => {
          const pattern = /\d\d\d\d-(1[0-2]|0[1-9])/;
          if (value && value.match(pattern)) return true
          return 'You must enter a date using the format YYYY-MM.'
        }
      ],
      tags: [
        value => {
          if (value && (value.length >= 1)) return true
          return 'You must enter at least one tag.'
        }
      ]
    }
  }),

  computed: {
    formTitle () {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item'
    },
    defaultItem () {
      return {
        group: (this.groupItems.length === 1) ? this.groupItems[0].id : undefined,
        id: '',
        project_name: '',
        project_number: '',
        deliverable_type: '',
        countries: [],
        therapeutic_area: '',
        customer: undefined,
        shared_with_customer: false,
        date: '',
        tags: [],
        originalFiles: [],
        files: [],
        newFiles: [],
        editable: true
      }
    }
  },

  watch: {
    dialog (val) {
      val || this.close()
    },
    dialogDelete (val) {
      val || this.closeDelete()
    },
    dialogShareWithCustomer (val) {
      val || this.closeShareWithCustomer()
    },
    dialogUnshareWithCustomer (val) {
      val || this.closeUnshareWithCustomer()
    },
    deliverables () {
      this.filterDeliverables()
    },
    selectedCustomers () {
      this.filterDeliverables()
    },
    selectedDeliverableTypes () {
      this.filterDeliverables()
    },
    selectedCountries () {
      this.filterDeliverables()
    },
    selectedTherapeuticAreas () {
      this.filterDeliverables()
    },
    selectedTags () {
      this.filterDeliverables()
    }
  },

  created () {
    this.initialize()
  },

  methods: {
    async loadCustomStudies() {
      let data = axios.get('/custom-studies',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {}
          });
      return data.then((response) => {
        this.deliverables = response.data.studies;
        this.groupItems = [...new Set(response.data.groups)].sort((g1, g2) => g1.name < g2.name);
        this.tagItems = [...new Set(response.data.tags)].sort();
        this.selectedTags = [...this.tagItems];
        this.customerItems = [...new Set(response.data.customers)].sort((c1, c2) => c1.name < c2.name);
        this.selectedCustomers = [...this.customerItems];
        this.therapeuticAreaItems = [...new Set(response.data.therapeutic_areas)].sort();
        this.selectedTherapeuticAreas = [...this.therapeuticAreaItems];
        this.countryItems = [...new Set(response.data.countries)].sort();
        this.selectedCountries = [...this.countryItems];
        this.deliverableTypeItems = [...new Set(response.data.deliverable_types)].sort();
        this.selectedDeliverableTypes = [...this.deliverableTypeItems];
      });
    },
    initialize () {
      this.loadCustomStudies().then(() => {
        console.log('Custom studies loaded')
        this.editedItem = this.defaultItem
        this.initialized = true
      })
    },

    getCustomer(customerId) {
      let customer = this.customerItems.find(c => c.id === customerId)
      return customer ? Object.assign({}, customer) : {
        id: customerId,
        name: 'Unknown'
      }
    },

    getGroup(groupId) {
      let group = this.groupItems.find(g => g.id === groupId)
      return group ? Object.assign({}, group) : {
        id: groupId,
        name: 'Unknown'
      }
    },

    editItem (item) {
      this.editedIndex = this.deliverables.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.editedItem.originalFiles = item.files
      this.failedSave = false
      this.dialog = true
    },

    deleteItem (item) {
      this.editedIndex = this.deliverables.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.failedDelete = false
      this.dialogDelete = true
    },

    shareWithCustomer (item) {
      this.editedIndex = this.deliverables.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.failedSharing = false
      this.dialogShareWithCustomer = true
    },

    unshareWithCustomer (item) {
      this.editedIndex = this.deliverables.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.failedRemoveSharing = false
      this.dialogUnshareWithCustomer = true
    },

    deleteItemConfirm () {
      this.dialogDisabled = true
      let data = axios.delete('/custom-study', {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
        params: {
          group: this.editedItem.group,
          study_id: this.editedItem.id
        }
      });
      data.then(() => {
        this.deliverables.splice(this.editedIndex, 1)
        // Note: For some reason need to call filterDeliverables() since the watch is not triggered by splice
        this.filterDeliverables()
        this.failedDelete = false
        this.closeDelete()
      }).catch(err => {
        this.failedDelete = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    shareWithCustomerItemConfirm () {
      this.dialogDisabled = true
      this.editedItem.shared_with_customer = true
      this.save().then(() => {
        this.failedSharing = false
        this.closeShareWithCustomer()
      }).catch(err => {
        this.failedSharing = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    unshareWithCustomerItemConfirm () {
      this.dialogDisabled = true
      this.editedItem.shared_with_customer = false
      this.save().then(() => {
        this.failedRemoveSharing = false
        this.closeUnshareWithCustomer()
      }).catch(err => {
        this.failedRemoveSharing = true
        console.log(err);
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    close () {
      this.dialog = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeDelete () {
      this.dialogDelete = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeShareWithCustomer () {
      this.dialogShareWithCustomer = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    closeUnshareWithCustomer () {
      this.dialogUnshareWithCustomer = false
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      })
    },

    validateAndSave () {
      this.dialogDisabled = true
      this.$refs.deliverable_form.validate().then(validationResult => {
        if (validationResult.valid) {
          return this.save().then(() => {
            this.failedSave = false
            this.close()
          }).catch(err => {
            this.failedSave = true
            console.log(err);
          });
        }
      }).finally(() => {
        this.dialogDisabled = false
      });
    },

    save () {
      if (!this.editedItem.newFiles) {
        this.editedItem.newFiles = []
      }
      if (!this.editedItem.files) {
        this.editedItem.files = []
      }
      if (!this.editedItem.originalFiles) {
        this.editedItem.originalFiles = []
      }
      let removedFiles = this.editedItem.originalFiles.filter(f => !this.editedItem.files.includes(f))

      let metaData = Object.assign({}, this.editedItem, {})
      delete metaData.id
      delete metaData.group
      delete metaData.files
      delete metaData.newFiles
      delete metaData.originalFiles
      delete metaData.shared_with_customer
      delete metaData.editable

      const formData = new FormData();
      formData.append("group", this.editedItem.group)
      if (this.editedItem.id) {
        formData.append("study_id", this.editedItem.id)
      }
      formData.append("metadata", JSON.stringify(metaData));
      this.editedItem.newFiles.forEach(f => {
        formData.append("files", f);
      })
      removedFiles.forEach(f => {
        formData.append("removed_files", f);
      })
      formData.append("shared_with_customer", this.editedItem.shared_with_customer)
      let data = axios.post('/custom-study', formData, {
        headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` }
      });
      return data.then((response) => {
        if (this.editedIndex > -1) {
          Object.assign(this.deliverables[this.editedIndex], response.data)
        } else {
          this.deliverables.push(response.data)
          // Note: For some reason need to call filterDeliverables() since the watch is not triggered by push
          this.filterDeliverables()
        }
        addItemsIfNotExists(this.tagItems, response.data.tags)
        addItemsIfNotExists(this.selectedTags, response.data.tags)
        addItemsIfNotExists(this.therapeuticAreaItems, response.data.therapeutic_area)
        addItemsIfNotExists(this.selectedTherapeuticAreas, response.data.therapeutic_area)
        addItemsIfNotExists(this.countryItems, response.data.countries)
        addItemsIfNotExists(this.selectedCountries, response.data.countries)
        addItemsIfNotExists(this.deliverableTypeItems, response.data.deliverable_type)
        addItemsIfNotExists(this.selectedDeliverableTypes, response.data.deliverable_type)
      });
    },

    openFile(group, id, file) {
      let data = axios.get('/custom-study',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              group: group,
              study_id: id,
              file_name: file
            }
          });
      data.then((response) => {
        window.open(response.data.url, '_blank')
      });
    },

    toggleAllNoneCustomers() {
      if (this.selectedCustomers.length === this.customerItems.length) {
        this.selectedCustomers = [];
      } else {
        this.selectedCustomers = [...this.customerItems]
      }
    },

    toggleAllOneCustomers(customer) {
      if ((this.selectedCustomers.length === 1) && (this.selectedCustomers[0].id === customer.id)) {
        this.selectedCustomers = [...this.customerItems]
      } else {
        this.selectedCustomers = [customer]
      }
    },

    toggleAllNoneDeliverableTypes() {
      if (this.selectedDeliverableTypes.length === this.deliverableTypeItems.length) {
        this.selectedDeliverableTypes = [];
      } else {
        this.selectedDeliverableTypes = [...this.deliverableTypeItems]
      }
    },
    
    toggleAllOneDeliverableTypes(deliverableType) {
      if ((this.selectedDeliverableTypes.length === 1) && (this.selectedDeliverableTypes[0] === deliverableType)) {
        this.selectedDeliverableTypes = this.deliverableTypeItems
      } else {
        this.selectedDeliverableTypes = [deliverableType]
      }
    },

    toggleAllNoneCountries() {
      if (this.selectedCountries.length === this.countryItems.length) {
        this.selectedCountries = [];
      } else {
        this.selectedCountries = [...this.countryItems]
      }
    },

    toggleAllOneCountries(country) {
      if ((this.selectedCountries.length === 1) && (this.selectedCountries[0] === country)) {
        this.selectedCountries = this.countryItems
      } else {
        this.selectedCountries = [country]
      }
    },

    toggleAllNoneTherapeuticAreas() {
      if (this.selectedTherapeuticAreas.length === this.therapeuticAreaItems.length) {
        this.selectedTherapeuticAreas = [];
      } else {
        this.selectedTherapeuticAreas = [...this.therapeuticAreaItems]
      }
    },

    toggleAllOneTherapeuticAreas(therapeuticArea) {
      if ((this.selectedTherapeuticAreas.length === 1) && (this.selectedTherapeuticAreas[0] === therapeuticArea)) {
        this.selectedTherapeuticAreas = this.therapeuticAreaItems
      } else {
        this.selectedTherapeuticAreas = [therapeuticArea]
      }
    },

    toggleAllNoneTags() {
      if (this.selectedTags.length === this.tagItems.length) {
        this.selectedTags = [];
      } else {
        this.selectedTags = [...this.tagItems]
      }
    },

    toggleAllOneTags(tag) {
      if ((this.selectedTags.length === 1) && (this.selectedTags[0] === tag)) {
        this.selectedTags = this.tagItems
      } else {
        this.selectedTags = [tag]
      }
    },

    filterDeliverables() {
      this.filteredDeliverables = this.deliverables.filter(item => {
        return this.selectedCustomers.some(c => c.id === item.customer)
            && this.selectedDeliverableTypes.includes(item.deliverable_type)
            && this.selectedCountries.some(c => item.countries.includes(c))
            && this.selectedTherapeuticAreas.includes(item.therapeutic_area)
            && this.selectedTags.some(t => item.tags.includes(t))
      })
    }
  }
})
</script>

<style lang="scss" scoped>
.dp__theme_dark {
  --dp-background-color: #364c5b;
  --dp-text-color: #dcdcdc;
  --dp-border-color: #dcdcdc;
}
.error {
  color: indianred;
}

@layer primevue {
  .p-progressbar {
    background-color: #364c5b;
  }
}
</style>
